import { Visibility, VisibilityOff } from '@mui/icons-material';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link, useLocation, useParams } from 'react-router';
import getOrganisation from 'apis/get-organisation';
import { ApiLogin } from 'apis/login';
import { FeatureContext } from 'contexts/feature-context';
import { UserContext } from 'contexts/user-context';
import { useInput, useBoolean, useStateful, useLogger } from 'hooks';
import Settings from 'settings/settings';
import Button from 'views/components/core/buttons/button';
import { DialogActions } from 'views/components/core/dialog';
import DialogInformationBox from 'views/components/core/dialog-information-box/dialog-information-box';
import FormControl from 'views/components/core/form-control';
import IconButton from 'views/components/core/icon-button';
import InputAdornment from 'views/components/core/input-adornment';
import Loading from 'views/components/core/loading';
import TextField from 'views/components/core/text-field';
import ResetPassword from 'views/pages/uam/reset-password';
import TwoFactorPhone from 'views/pages/uam/two-factor/two-factor-phone';
import 'views/layouts/login/login.scss';
import 'views/pages/uam/uam-styles.scss';
import NotificationService from 'utils/notification';
import Constants from 'utils/constants';
import { AllRoutes } from '@utils/router/all-routes';

const Login = () => {
    const { user, token, setToken, setUser, isLoggedIn, setIsLoggedIn, setPasswordForResetPage, passwordForResetPage } =
        useContext(UserContext);
    const { hasFeature } = useContext(FeatureContext);
    const navigate = useNavigate();
    const { orgCode } = useParams();
    const Logger = useLogger();
    const { t } = useTranslation();
    const email = useInput('');
    const [password, setPassword] = useState('');
    const [organisation, setOrganisation] = useState(null);
    const loading = useBoolean(false);
    const disabledSaveButton = useBoolean(false);
    const emailError = useStateful('');
    const passwordError = useStateful('');
    const showPassword = useBoolean(false);
    const showResetPage = useBoolean(false);
    const error = useStateful('');
    const showSuccessfulRegistration = useBoolean(false);
    const showVerifiedEmail = useBoolean(false);
    const twoFactorPhoneShow = useBoolean(false);
    const showAfterForceLogout = useBoolean(false);
    const showEmailVerificationRequired = useBoolean(false);
    const showMaxAttempts = useBoolean(false);

    const { search } = useLocation();
    const queryParams = useMemo(() => new URLSearchParams(search), [search]);

    useEffect(() => {
        const { environment } = window;
        if (environment !== 'LOCAL') {
            // Log the current version to the console
            console.log('-------------------------------------');
            console.log('OPRA 5 Web Application:');
            console.log('      Version=', import.meta.env.VITE_VERSION);
            console.log('  Environment=', environment);
            console.log('-------------------------------------');
        }
    }, []);

    useEffect(() => {
        const ssoToken = queryParams.get('token');
        if (ssoToken) {
            setToken(ssoToken);
            navigate(AllRoutes.path('home'));
        }
    }, [queryParams]);

    const isSso = organisation?.security?.authenticationMethod === Constants.AuthenticationMethods.SSO;

    const ssoAuthenticate = () => {
        if (isSso) {
            const targetLink = `${Settings.api.url()}/api/login/ssoreply?orgCode=${orgCode}`;
            const oneLoginUrl = `${organisation?.ssoProviderUrl}/access/initiate?iss=${encodeURIComponent(
                organisation?.security?.ssoIssuer,
            )}&target_link_uri=${encodeURIComponent(targetLink)}`;
            window.location.assign(oneLoginUrl);
        }
    };

    const loadOrganisation = () => {
        loading.setTrue();

        getOrganisation(orgCode)
            .then((org) => {
                setOrganisation(org);
                loading.setFalse();
            })
            .catch((e) => {
                NotificationService.showNotification(e.message);
            });
    };

    useEffect(() => {
        if (orgCode) loadOrganisation();
    }, [orgCode]);

    useEffect(() => {
        if (user && token && isLoggedIn) {
            if (user.forceResetPassword) showResetPage.setTrue();
            if (!user.forceResetPassword) navigate(AllRoutes.path('home'));
        }

        const params = new URLSearchParams(window.location.search);

        if (params.get('complete')) showSuccessfulRegistration.setTrue();
        else if (params.get('verified')) showVerifiedEmail.setTrue();
        else if (params.get('forceLogout')) showAfterForceLogout.setTrue();
        else if (params.get('email-verification')) showEmailVerificationRequired.setTrue();
        else if (params.get('max-attempts-failed')) showMaxAttempts.setTrue();
    }, [user, token, navigate, isLoggedIn, window.location.search]);

    const emailValidation = (isRequired?: boolean): boolean => {
        let isValid = true;
        if (!email.value && isRequired) {
            isValid = false;
            emailError.setValue(t('ra:requiredFieldError'));
        } else {
            emailError.setValue('');
        }
        return isValid;
    };

    const passwordValidation = (isRequired?: boolean): boolean => {
        let isValid = true;
        if (!password && isRequired) {
            isValid = false;
            passwordError.setValue(t('ra:requiredFieldError'));
        } else {
            passwordError.setValue('');
        }
        return isValid;
    };

    const sendLoginRequest = async (passwordRequest: string, phoneRequest?: string) => {
        loading.setTrue();
        const response = await ApiLogin(email.value || user.email, passwordRequest, orgCode, phoneRequest);
        const phone = phoneRequest ?? response.phone;
        const last4 = phone?.length >= 4 ? phone?.substring(phone.length - 4) : (phone ?? 'xxxx');
        if (response) loading.setFalse();

        if (response.error) {
            if (response.error === 'Failed to fetch' || response.error.indexOf('Unexpected token < in JSON') >= 0) {
                error.setValue('Unable to contact the server. Please try again later.');
            } else if (response.error === 'login:loginUnknownUserPassword') {
                emailError.setValue(t('login:loginUnknownUserPassword'));
                passwordError.setValue(t('login:loginUnknownUserPassword'));
            } else {
                error.setValue(response.error);
            }
        }

        if (response.token && response.state !== '2fa' && response.state !== '2fa-phone') {
            setIsLoggedIn(true);
            setToken(response.token);
            // Logger.log(`User logged in (${email.value}) with token: ${response.token}`);
        } else if (response.state === '2fa-phone') twoFactorPhoneShow.setTrue();
        else if (response.state === '2fa') {
            // Get last 4 digits of their phone
            setToken(null);
            setUser(null);

            if (last4?.length && response.token?.length) {
                if (orgCode) navigate(`/2fa/${response.token}/${last4}/${orgCode}`);
                else navigate(`/2fa/${response.token}/${last4}`);
            }
        } else {
            setToken(null);
            setUser(null);
        }
    };

    const submitTwoFactorPhone = (phone: string) => {
        sendLoginRequest(password, phone);
    };

    const submitLogin = (e: any) => {
        e.preventDefault();
        if (emailValidation(true) && passwordValidation(true)) {
            // clear loginError from previous attempt
            error.setValue('');
            sendLoginRequest(password);
        }
    };

    useEffect(() => {
        emailValidation();
    }, [email]);

    useEffect(() => {
        passwordValidation();
    }, [password]);

    const getHeadingText = () => {
        if (error.value || showMaxAttempts.value) return t('login:signInFailed');

        if (showEmailVerificationRequired.value) return t('login:emailAddressVerificationRequired');

        if (showVerifiedEmail.value) return t('login:emailAddressVerified');

        return t('login:signIn');
    };

    const loginStandardForm = (
        <form className="login-page-form" onSubmit={submitLogin}>
            <FormControl className="form-control mb error" fullWidth required>
                <TextField
                    error={!!emailError.value}
                    label={`${t('email')}*`}
                    id="email"
                    type="email"
                    minRows={1}
                    autoFocus
                    fullWidth
                    value={email.value}
                    onChange={(event: any) => {
                        disabledSaveButton.setFalse();
                        emailError.setValue('');
                        passwordError.setValue('');
                        email.onChange(event);
                    }}
                    placeholder={t('enterYourEmail')}
                    variant="outlined"
                    color="secondary"
                    helperText={emailError.value ?? null}
                />
            </FormControl>

            <FormControl className="form-control error" fullWidth required style={{ marginBottom: 20 }}>
                <TextField
                    error={!!passwordError.value}
                    label={`${t('password')}*`}
                    id="password"
                    type={showPassword.value ? 'text' : 'password'}
                    minRows={1}
                    fullWidth
                    value={password}
                    onChange={(event: any) => {
                        disabledSaveButton.setFalse();
                        emailError.setValue('');
                        passwordError.setValue('');
                        setPassword(event.target.value);
                        setPasswordForResetPage(event.target.value);
                    }}
                    placeholder={t('enterYourPassword')}
                    variant="outlined"
                    color="secondary"
                    helperText={passwordError.value ? t(passwordError.value) : null}
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        id="toggle-password-visibility"
                                        aria-label={t('togglePasswordVisibility')}
                                        onClick={showPassword.toggle}
                                        onMouseDown={(e: any) => e.preventDefault()}
                                    >
                                        {showPassword.value ? <Visibility /> : <VisibilityOff />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        },
                    }}
                />
            </FormControl>
            <div className="error-notification">{error.value ? t(error.value) : null}</div>
            <DialogActions
                style={{ paddingTop: 20, paddingBottom: 10 }}
                className="actions-info-wrapper-login-page flex-end"
            >
                <Link
                    id="login-forgot-password"
                    to={AllRoutes.path(orgCode ? 'forgot-password' : 'forgot-password-admin', { orgCode })}
                >
                    {t('login:forgottenPassword')}
                </Link>

                <Button
                    className="login-submit-button"
                    disabled={disabledSaveButton.value || !!emailError.value || !!passwordError.value}
                    text="login:signIn"
                    id="login-signin"
                    type="submit"
                />
            </DialogActions>
        </form>
    );

    const loginSsoForm = (
        <div className="login-page-form">
            {t('login:ssoInfoText')}
            {isSso && <div className="sso-error-notification">{t(queryParams.get('error'))}</div>}
            <DialogActions
                style={{ paddingTop: 20, paddingBottom: 10 }}
                className="actions-info-wrapper-login-page flex-end"
            >
                <Button
                    className="sso-login-submit-button"
                    disabled={disabledSaveButton.value}
                    text="login:authenticate"
                    id="login-authenticate"
                    type="redirect"
                    onClick={ssoAuthenticate}
                />
            </DialogActions>
        </div>
    );

    if (loading.value)
        return (
            <div className="loading-spinner-container">
                <Loading inside />
            </div>
        );

    if (!isSso && showResetPage.value)
        return <ResetPassword currentPassword={passwordForResetPage} orgCode={orgCode} organisation={organisation} />;

    if (!isSso && twoFactorPhoneShow.value) return <TwoFactorPhone submitTwoFactorPhone={submitTwoFactorPhone} />;

    return (
        <div className="login-page">
            <h1 className="login-title">{getHeadingText()}</h1>
            {showSuccessfulRegistration.value && !error.value && (
                <DialogInformationBox topText={t('login:registrationSuccessfulMessage')} />
            )}
            {showVerifiedEmail.value && !error.value && (
                <DialogInformationBox
                    topText={t('login:emailVerifiedMessage')}
                    bottomText={t('login:wouldYouLikeSignIn')}
                />
            )}
            {(showMaxAttempts.value || error.value === 'login:loginTooManyFailedLogins') && (
                <DialogInformationBox
                    topText={t('login:theMaximumNumberOfLoginAttempts')}
                    bottomText={t('login:pleaseTryAgainInMinutes', {
                        minutes: orgCode ? organisation?.security.lockoutPeriodMinutes : 10,
                    })}
                />
            )}

            {showAfterForceLogout.value && !error.value && (
                <DialogInformationBox topText={t('login:afterForceLogoutLoginMessage')} />
            )}

            {showEmailVerificationRequired.value && !error.value && (
                <DialogInformationBox
                    topText={
                        !isSso ? t('login:youHaveUpdatedYourEmailAddress') : t('login:yourEmailAddressHasBeenUpdated')
                    }
                    bottomText={
                        !isSso ? t('login:pleaseCheckYourNewEmail') : t('login:clickAuthenticateToVerifyChange')
                    }
                />
            )}

            {!showEmailVerificationRequired.value && !isSso && loginStandardForm}
            {isSso && loginSsoForm}
        </div>
    );
};

export default Login;
